import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import { CompetitionStatusEnum, RankByEnum, RankingStatusEnum } from '@flipster/apis/v1/tradingCompetition/types';
import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Button } from '@flipster/react/design-system/button';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { OrdinalNumber } from '@flipster/react/design-utils/ordinal-number';
import QuestionCircleIcon from '@flipster/react/icon/generated/QuestionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { divider, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { Price } from '@flipster/ui/Price';
import { UsdPrecision, UsdtCurrency } from '@flipster/utils/constants/currency';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';
import webviewHandler from '@flipster/utils/webview-handler';

import { useAuth } from '../../../shared/auth/state';
import { showErrorDialog } from '../../../shared/show-error-dialog';
import { competitionBunja, competitionRankingBunja } from '../../bunja';
import { PrizeClaim } from './prize-claim';

const rankingByText = {
  [RankByEnum.Pnl]: 'P&L',
  [RankByEnum.Volume]: 'Volume',
};

export const UserInfo = () => {
  const t = useTranslations();
  const { user } = useAuth();
  const { id, competitionAtom, deregisterCompetition, claimRequiredAtom } = useBunja(competitionBunja);
  const { myRankingAtom, isLessThanAwardListAtom } = useBunja(competitionRankingBunja);
  const competition = useAtomValue(competitionAtom);
  const myRanking = useAtomValue(myRankingAtom);
  const isLessThanAwardList = useAtomValue(isLessThanAwardListAtom);
  const claimRequired = useAtomValue(claimRequiredAtom);

  if (!competition) return null;

  const onClickCancelRegistration = () => {
    dialog.open({
      title: {
        label: 'Notice',
      },
      content: 'Are you sure you want to cancel your participation?',
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label='Yes'
            onClick={async () => {
              segment.track(CompetitionsEvent.CompetitionRankingPageCancelRegistrationClick, { competition_id: id });
              try {
                await deregisterCompetition();
              } catch (err) {
                const errorCode = getErrorCode(err);
                const errorMessage = getErrorMessage(err);
                if (errorCode === 'api.unauthorized') {
                  dialog.open({
                    title: {
                      label: 'Notice',
                    },
                    content: errorMessage,
                    buttons: {
                      primary: (
                        <Dialog.Button
                          hierarchy='primary'
                          label='Confirm'
                          onClick={() => {
                            webviewHandler.postMessage({ action: 'link', params: { page: 'login' } });
                          }}
                        />
                      ),
                    },
                  });
                } else showErrorDialog(err);
              }
            }}
          />
        ),
        secondary: <Dialog.Button hierarchy='secondary' color='neutral' label='Cancel' />,
      },
    });
  };

  const isUnfulfilled =
    competition?.competitionType != 'WEEKLY_PNL' && // recurring tc
    myRanking?.rankingStatus === RankingStatusEnum.Unfulfilled &&
    !isLessThanAwardList;
  const showCancelRegistration =
    competition.competitionType != 'WEEKLY_PNL' && // recurring tc
    competition.competitionStatus !== CompetitionStatusEnum.Finished;

  return (
    <div
      className={stack({
        textStyle: 'Title/XS',
        w: '100%',
        color: 'content.default.level-5',
        bg: '#23539e', // legacy BLUE_DARK. should be changed
        gap: 16,
        tabletDown: { p: 16 },
        tablet: {
          maxW: 472,
          p: '24px 30px',
        },
      })}
    >
      <div>
        <div
          className={hstack({
            justifyContent: 'space-between',
            mb: 6,
          })}
          data-test-id='tc_user_info_actions'
        >
          <Link href='/user/profile' className={css({ textStyle: 'Title/S', color: 'content.default.level-5' })}>
            {user?.nickname}
          </Link>
          {showCancelRegistration && (
            <Button
              hierarchy='primary'
              size='medium'
              color='sell'
              onClick={onClickCancelRegistration}
              label={t('Autogen_common.r251126.s_Cancel_registration')}
            />
          )}
        </div>
        <dl
          className={hstack({
            '& dt': {
              display: 'flex',
              alignItems: 'center',
              textStyle: 'Text/L/Regular',
              mb: 2,
            },
          })}
          data-test-id='tc_user_info_my_info'
        >
          <div
            className={css({
              borderRightWidth: '1px',
              borderRightStyle: 'solid',
              borderColor: 'border.default.level-4',
              tabletDown: {
                pr: 16,
                mr: 16,
              },
              tablet: {
                pr: 30,
                mr: 30,
              },
            })}
          >
            <dt>
              {isUnfulfilled ? (
                <>
                  <span
                    className={css({
                      textStyle: 'Text/XS/Bold',
                      p: '2px 10px',
                      mr: 4,
                      bg: 'background.accent.sell',
                      rounded: 4,
                    })}
                  >
                    <FormattedMessage id='Autogen_common.r251126.s_Unfulfilled' />
                  </span>
                  <Tooltip placement='bottom' maxWidth={300}>
                    <Tooltip.Trigger css={{ display: 'inline-flex' }}>
                      <QuestionCircleIcon size={16} color={token('colors.content.default.level-3')} />
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                      <FormattedMessage id='Autogen_common.r251126.s_Displayed_when_minimum_trading_volume_USD_does_not_meet_the_prize_condition' />
                    </Tooltip.Content>
                  </Tooltip>
                </>
              ) : (
                t('Autogen_common.r251126.s_Ranking')
              )}
            </dt>
            <dd>
              <OrdinalNumber value={!isLessThanAwardList ? myRanking?.rank : undefined} max={3} />
            </dd>
          </div>
          <div>
            <dt>{rankingByText[competition.rankBy]}</dt>
            <dd>
              <Price
                price={competition.rankBy === RankByEnum.Pnl ? myRanking?.pnl : myRanking?.volume}
                zeroString='-'
                currency={UsdtCurrency}
                precision={UsdPrecision}
                withColor
              />
            </dd>
          </div>
        </dl>
      </div>
      {claimRequired && (
        <>
          <hr className={divider({ orientation: 'horizontal', color: 'border.interaction.disabled' })} />
          <PrizeClaim />
        </>
      )}
    </div>
  );
};
